import { useState, useEffect, useRef } from "react";
import { Box, Container, Grid, Typography, Card } from "@mui/material";
import { Users, TrendingUp, Award, Globe } from "lucide-react";

const stats = [
  {
    icon: <Users size={32} />,
    endValue: 40,
    prefix: "$",
    suffix: "M+",
    label: "Paid to Sellers",
    description: "Earned by our members",
  },
  {
    icon: <TrendingUp size={32} />,
    prefix: "",
    endValue: 25000,
    suffix: "",
    label: "unique SKU’s purchased ",
    description: "Wide variety of accepted items",
  },
  {
    icon: <Globe size={32} />,
    endValue: 50,
    suffix: " states",
    label: "Nationwide Presence",
    description: "With logistics partners across all states",
  },
];

const Counter = ({
  start = 0,
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
}) => {
  const [count, setCount] = useState(start);
  const countRef = useRef(count);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const steps = 60;
    const increment = (end - start) / steps;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const nextValue = Math.min(start + increment * currentStep, end);
      setCount(Math.floor(nextValue));
      countRef.current = nextValue;

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [start, end, duration, isVisible]);

  return (
    <Typography
      ref={elementRef}
      variant="h2"
      sx={{
        fontSize: { xs: "2rem", md: "2.5rem" },
        fontWeight: "bold",
        mb: 0.5,
        fontFamily: '"Inter SemiBold", Arial, sans-serif',
        color: "white",
      }}
    >
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </Typography>
  );
};

export default function About() {
  return (
    <Box
      id="about"
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg, #101010 0%, #1e1e1e 100%)",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontSize: { xs: "2.5rem", md: "3rem" },
              fontWeight: "bold",
              mb: 3,
              background: "linear-gradient(45deg, #ffffff, #00ADB5)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About RSC
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "text.secondary",
              fontSize: "1.1rem",
              lineHeight: 1.8,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            RSC is a next-generation marketplace that simplifies selling by
            offering instant cash, seamless logistics, and the best market
            rates. Unlike traditional platforms, we eliminate the hassle of
            listings, negotiations, and unreliable buyers—giving you a
            frictionless way to turn your items into cash.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1.1rem",
              lineHeight: 1.8,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Our mission is to revolutionize the selling experience by providing
            a secure, transparent, and efficient platform that ensures every
            transaction is smooth, profitable, and hassle-free. With
            cutting-edge technology and a commitment to customer satisfaction,
            we make selling as easy as it should be.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  bgcolor: "transparent",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  transition: "all 0.3s ease-in-out",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    borderColor: "white",
                    transform: "translateY(-5px)",
                    "& .icon-box": {
                      transform: "scale(1.1)",
                    },
                    "& .description": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <Box
                  className="icon-box"
                  sx={{
                    color: "white",
                    mb: 2,
                    transition: "transform 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {stat.icon}
                </Box>
                <Counter
                  end={stat.endValue}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    mb: 2,
                    fontSize: "1rem",
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  className="description"
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    opacity: 0.7,
                    transform: "translateY(10px)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {stat.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
